import { Controller } from "@hotwired/stimulus";
import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import Dashboard from "@uppy/dashboard";
import Form from "@uppy/form";
import Spanish from "@uppy/locales/lib/es_MX";

export default class extends Controller {
  static targets = [
    "mainInput",
    "form",
    "mainPreview",
    "submitButton",
    "galleryInput",
    "galleryPreview",
  ];
  static values = {
    multiple: Boolean,
    minFileSize: Number,
    maxFileSize: Number,
    allowedFileTypes: Array,
    shouldDisableSubmitButtonAtStart: {
      type: Boolean,
      default: true,
    },
    maxFiles: Number,
  };

  connect() {
    document.addEventListener("turbo:morph", this.#afterMorphStartCheck);
    this.#start();
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.#afterMorphStartCheck);

    this.#cleanup();
  }

  #start = () => {
    if (!this.hasMainPreviewTarget && !this.hasGalleryPreviewTarget) {
      throw new Error("No preview targets found");
    }

    if (this.hasMainPreviewTarget && this.hasMainInputTarget) {
      this.initMainImageUploader();
    }

    if (this.hasGalleryPreviewTarget && this.hasGalleryInputTarget) {
      this.initGalleryUploader();
    }

    if (
      this.hasSubmitButtonTarget &&
      this.shouldDisableSubmitButtonAtStartValue
    ) {
      this.submitButtonTarget.disabled = true;
    }
  };

  #cleanup = () => {
    if (this.mainUppy) {
      this.mainUppy.destroy();
    }
    if (this.galleryUppy) {
      this.galleryUppy.destroy();
    }
  };

  #afterMorphStartCheck = () => {
    this.#cleanup();
    this.#start();
  };

  initMainImageUploader() {
    this.mainUppy = new Uppy({
      id: "mainImageUppy",
      autoProceed: true,
      locale: Spanish,
      restrictions: {
        minFileSize: this.minFileSizeValue || 10 * 1024,
        maxFileSize: this.maxFileSizeValue || 10 * 1024 * 1024,
        allowedFileTypes: this.allowedFileTypesValue || ["image/*"],
        maxNumberOfFiles: 1,
      },
    })
      .use(Form, {
        target: this.formTarget,
      })
      .use(Dashboard, {
        inline: true,
        target: this.mainPreviewTarget,
        proudlyDisplayPoweredByUppy: false,
        height: 200,
        width: "100%",
        showProgressDetails: true,
        showRemoveButtonAfterComplete: true,
        single: true, // Only allow one file
      })
      .use(AwsS3, {
        endpoint: "/",
      });

    this.mainUppy.on("upload-start", () => {
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = true;
      }
    });

    this.mainUppy.on("upload-success", (file, response) => {
      const url = response.uploadURL || response.body.location;
      const fullPath = url.split(/\.com\//)[1];
      const key = decodeURIComponent(fullPath);

      const uploadedFileData = {
        id: key.replace(/^cache\//, ""),
        storage: "cache",
        metadata: {
          size: file.size,
          filename: file.name,
          mime_type: file.type,
        },
      };

      this.mainInputTarget.value = JSON.stringify(uploadedFileData);

      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false;
      }
    });

    this.mainUppy.on("upload-error", () => {
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false;
      }
    });

    this.mainUppy.on("file-removed", () => {
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = true;
      }
    });
  }

  initGalleryUploader() {
    this.galleryUppy = new Uppy({
      id: "galleryUppy",
      autoProceed: true,
      locale: Spanish,
      restrictions: {
        minFileSize: this.minFileSizeValue || 10 * 1024,
        maxFileSize: this.maxFileSizeValue || 10 * 1024 * 1024,
        allowedFileTypes: this.allowedFileTypesValue || ["image/*"],
        maxNumberOfFiles: this.hasMaxFilesValue ? this.maxFilesValue : null,
      },
    })
      .use(Form, {
        target: this.formTarget,
      })
      .use(Dashboard, {
        inline: true,
        target: this.galleryPreviewTarget,
        proudlyDisplayPoweredByUppy: false,
        height: 300,
        width: "100%",
        showProgressDetails: true,
        showRemoveButtonAfterComplete: true,
      })
      .use(AwsS3, {
        endpoint: "/",
      });

    this.galleryUppy.on("upload-start", () => {
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = true;
      }
    });

    this.galleryUppy.on("upload-success", (file, response) => {
      const url = response.uploadURL || response.body.location;
      const fullPath = url.split(/\.com\//)[1];
      const key = decodeURIComponent(fullPath);

      const uploadedFileData = {
        id: key.replace(/^cache\//, ""),
        storage: "cache",
        metadata: {
          size: file.size,
          filename: file.name,
          mime_type: file.type,
        },
      };

      // For gallery images, we need to handle multiple files
      const currentValue = this.galleryInputTarget.value;
      const currentFiles = currentValue ? JSON.parse(currentValue) : [];
      currentFiles.push(uploadedFileData);
      this.galleryInputTarget.value = JSON.stringify(currentFiles);
    });

    // Handle all uploads complete
    this.galleryUppy.on("complete", () => {
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false;
      }
    });

    // Handle file removal for gallery
    this.galleryUppy.on("file-removed", (file) => {
      const currentValue = this.galleryInputTarget.value;
      if (currentValue) {
        const currentFiles = JSON.parse(currentValue);
        const updatedFiles = currentFiles.filter(
          (f) => f.metadata.filename !== file.name
        );
        this.galleryInputTarget.value = JSON.stringify(updatedFiles);
      }
    });

    this.galleryUppy.on("upload-error", () => {
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false;
      }
    });
  }
}
